// src/config.js

// Bypass ngrok browser warning for all fetch requests
const originalFetch = window.fetch;
window.fetch = (...args) => {
  let [url, config] = args;
  // Check if the URL is to an ngrok domain
  if (typeof url === "string" && url.includes("ngrok-free.app")) {
    config = config || {};
    config.headers = config.headers || {};
    if (config.headers instanceof Headers) {
      config.headers.set("ngrok-skip-browser-warning", "true");
    } else {
      config.headers["ngrok-skip-browser-warning"] = "true";
    }
    args[1] = config;
  }
  return originalFetch(...args);
};

const getApiBaseUrl = () => {
  // Build-time env var — set this per environment via:
  //   REACT_APP_API_BASE_URL=https://your-api/ npm run build
  // or via a .env / .env.production file (project root).
  // Production reference: https://api-incubation.worksqr.com/
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }

  // Fallback: assume the API is reachable on the same host that served
  // the page. Works for the common nginx setup where /api is proxied
  // to the backend, and gives a sensible default for local dev too.
  // If wrong, API calls fail (visible in the network tab) — never a
  // blank page.
  const { protocol, hostname } = window.location;
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return process.env.REACT_APP_API_BASE_URL;
  }
  // eslint-disable-next-line no-console
  console.warn(
    "REACT_APP_API_BASE_URL not set; defaulting to same-origin. " +
      "Set it at build time to point at the backend API."
  );
  return `${protocol}//${hostname}`;
};

export const API_BASE_URL = getApiBaseUrl();

// Origin used when generating public shareable links (CFA apply, event preview).
// We can't use window.location.origin at link-generation time because CRA picks
// whatever PORT was free, so a link minted on :3005 becomes useless on :3000.
// Set REACT_APP_PUBLIC_ORIGIN per environment to pin the host the public
// links resolve to.
export const PUBLIC_ORIGIN =
  process.env.REACT_APP_PUBLIC_ORIGIN ||
  (typeof window !== "undefined" ? window.location.origin : "");

// Domain and subdomain utilities
export const getCurrentSubdomain = () => {
  const hostname = window.location.hostname;

  // Skip for localhost and IP addresses (network access)
  if (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "0.0.0.0"
  ) {
    return null;
  }

  // Skip for IP addresses (e.g., 172.16.17.204)
  if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return null;
  }

  const parts = hostname.split(".");

  // If less than 3 parts, no subdomain (e.g., domain.com)
  if (parts.length < 3) {
    return null;
  }

  // First part is the subdomain
  return parts[0];
};

export const getCurrentDomain = () => {
  return window.location.hostname;
};

export const isSubdomainAccess = () => {
  return getCurrentSubdomain() !== null;
};

// Application Configuration
export const APP_CONFIG = {
  DEFAULT_TIMEZONE: "Asia/Kolkata",
  DEFAULT_CURRENCY: "INR",
  CURRENCY_SYMBOL: "₹",
  DATE_FORMAT: "DD/MM/YYYY",
  TIME_FORMAT: "HH:mm",
  DATETIME_FORMAT: "DD/MM/YYYY HH:mm",
};

// Currency formatting function
export const formatCurrency = (
  amount,
  currency = APP_CONFIG.DEFAULT_CURRENCY,
) => {
  if (currency === "INR") {
    return `₹${amount.toLocaleString("en-IN")}`;
  }
  return `$${amount.toLocaleString()}`;
};

// Date formatting function for Indian timezone
export const formatDateTime = (date, format = APP_CONFIG.DATETIME_FORMAT) => {
  const options = {
    timeZone: APP_CONFIG.DEFAULT_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  };

  return new Intl.DateTimeFormat("en-IN", options).format(new Date(date));
};
